'use client';

import Link, { useLinkStatus } from 'next/link';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useEffect, useRef } from 'react';
import { UserPill } from './UserPill';
import { StockMenuTab } from './StockMenu';
import { trackNavEvent } from '@/lib/analytics/counterscale';

export type Tab = { id: string; label: string; href: string };

/* Production nav set, single source of truth. The wordmark covers home,
   so home is no longer a tab. /briefs is the editorial archive (today's
   feature lives on /). */
// Temporary nav-link gate. The /cars page is still live (reachable by direct
// URL); it's just hidden from the nav while it's being built out. Flip to true
// to surface the tab — no other change needed.
const SHOW_CARS_TAB = true;

export const PROD_TABS: Tab[] = [
  // `stock` is a menu trigger, not a link — Nav special-cases id === 'stock'
  // and renders <StockMenuTab>. The href is only used for active-tab
  // matching (lights up on any /stock/* route via the prefix rule below).
  { id: 'stock', label: 'stock locator', href: '/stock' },
  { id: 'briefs', label: 'briefs', href: '/briefs' },
  { id: 'compare', label: 'compare', href: '/compare' },
  ...(SHOW_CARS_TAB
    ? [{ id: 'cars', label: 'cars', href: '/cars' } as Tab]
    : []),
  { id: 'reviews', label: 'reviews', href: '/channel/all' },
  { id: 'games', label: 'games', href: '/games' },
];

function activeTabFromPath(pathname: string | null, tabs: Tab[]): string {
  // Empty string means "no tab active" — used for surfaces like the prod
  // homepage `/` (covered by the wordmark) where lighting up a tab would
  // be a lie. The previous fallback returned tabs[0].id, which made
  // briefs (the first PROD tab) appear active on `/`.
  if (!pathname) return '';
  if (pathname.startsWith('/channel')) {
    const hit = tabs.find((t) => t.id === 'reviews');
    if (hit) return hit.id;
  }
  // Longest-href match wins so '/' (home) doesn't shadow '/stock/...'
  const sorted = [...tabs].sort((a, b) => b.href.length - a.href.length);
  const hit = sorted.find(
    (t) => pathname === t.href || pathname.startsWith(`${t.href}/`),
  );
  return hit?.id ?? '';
}

/* Wordmark — Playfair Display, italic motoring + roman obsession.
   Emits both the long lockup (≥480px) and a short "m//o" lockup
   (<480px). CSS in tokens.css toggles which is visible. The short
   lockup echoes the active-tab `// ` marker — italic m + coral mono
   // + roman coral o. The aria-label is unchanged so screen readers
   always announce "Motoring Obsession home". */
export function Wordmark() {
  return (
    <Link
      href="/"
      aria-label="Motoring Obsession home"
      className="mo-nav-wordmark"
      onClick={() => trackNavEvent('logo')}
    >
      <span className="wm-long">
        <span className="wm-m">motoring</span>
        <span className="wm-o">obsession</span>
      </span>
      <span className="wm-short" aria-hidden>
        <span className="wm-m-short">m</span>
        <span className="wm-slash">//</span>
        <span className="wm-o-short">o</span>
      </span>
    </Link>
  );
}

/* A tab's label, marked pending from the click until the new page arrives.
   Pages render per request and send nothing until they're done, so without
   this a click on a slow page (/compare cold ≈ 1 s) looked like it did
   nothing. useLinkStatus is per-<Link>, so only the clicked tab lights up. */
function TabLabel({ label }: { label: string }) {
  const { pending } = useLinkStatus();
  return (
    <span
      className={pending ? 'mo-nav-tab-label is-pending' : 'mo-nav-tab-label'}
    >
      {label}
    </span>
  );
}

export function SignInButton() {
  return (
    <Link
      href="/auth/signin"
      className="mo-nav-signin"
      onClick={() => trackNavEvent('signin')}
    >
      sign in
    </Link>
  );
}

/* Default right slot — UserPill when signed in, sign-in CTA otherwise.
   Renders as a thin shell so the slot reserves space during session
   hydration (status === 'loading') and avoids layout shift. */
function DefaultRightSlot() {
  const { status } = useSession();
  if (status === 'loading') {
    return (
      <span
        className="mo-nav-signin"
        aria-hidden
        style={{ visibility: 'hidden' }}
      >
        sign in
      </span>
    );
  }
  if (status === 'authenticated') return <UserPill />;
  return <SignInButton />;
}

type NavProps = {
  /** 'page' (default, borderless) | 'rule' (hairline divider) | 'denim' (hero takeover) */
  skin?: 'page' | 'rule' | 'denim';
  /** Right-slot content — defaults to <SignInButton />. Pass null to omit. */
  rightSlot?: React.ReactNode;
  /** Tabs to render. Production callers pass PROD_TABS. */
  tabs: Tab[];
};

export function Nav({ skin = 'page', rightSlot, tabs }: NavProps) {
  const pathname = usePathname();
  const active = activeTabFromPath(pathname, tabs);
  const right = rightSlot === undefined ? <DefaultRightSlot /> : rightSlot;
  const tabsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    const strip = tabsRef.current;
    if (!strip) return;
    const onTab = strip.querySelector<HTMLElement>('.mo-nav-tab-on');
    if (!onTab) return;
    onTab.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [active]);

  return (
    <div className={`mo-nav-shell skin-${skin}`}>
      <nav className="mo-nav-pill">
        <Wordmark />

        <div className="mo-nav-row-tabs" role="tablist" ref={tabsRef}>
          {tabs.map((t) => {
            const on = active === t.id;
            if (t.id === 'stock') {
              return <StockMenuTab key={t.id} label={t.label} active={on} />;
            }
            return (
              <Link
                key={t.id}
                href={t.href}
                role="tab"
                aria-selected={on}
                className={on ? 'mo-nav-tab mo-nav-tab-on' : 'mo-nav-tab'}
                onClick={() => trackNavEvent(t.id)}
              >
                <TabLabel label={t.label} />
              </Link>
            );
          })}
        </div>

        {right}
      </nav>
    </div>
  );
}
